import React from 'react';

import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment, MenuItem, Stack, TextField } from '@mui/material';

import {
  TRANSLATION_OVERVIEW_STATUSES,
  TRANSLATION_OVERVIEW_UPLOAD_FILTERS,
  getStatusLabel,
  getUploadStateLabel,
} from './translationOverviewUtils';
import type { TranslationOverviewStatus, TranslationOverviewUploadFilter } from './types';

interface Props {
  search: string;
  onSearchChange: (search: string) => void;
  searchPlaceholder: string;
  status: TranslationOverviewStatus | '';
  onStatusChange: (status: TranslationOverviewStatus | '') => void;
  brand?: string;
  brandOptions?: Array<string>;
  onBrandChange?: (brand: string) => void;
  /** Omitted by the drill-in drawers, which are already scoped to a single row. */
  uploadStatus?: TranslationOverviewUploadFilter | '';
  onUploadStatusChange?: (uploadStatus: TranslationOverviewUploadFilter | '') => void;
}

export default function TranslationOverviewToolbar({
  search,
  onSearchChange,
  searchPlaceholder,
  status,
  onStatusChange,
  brand,
  brandOptions,
  onBrandChange,
  uploadStatus,
  onUploadStatusChange,
}: Props) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
      <TextField
        size="small"
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder={searchPlaceholder}
        sx={{ minWidth: 260 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      <TextField
        select
        size="small"
        label="Status"
        value={status}
        onChange={(event) => onStatusChange(event.target.value as TranslationOverviewStatus | '')}
        sx={{ minWidth: 180 }}
        helperText="Rows with at least one pair in this status"
      >
        <MenuItem value="">All statuses</MenuItem>
        {TRANSLATION_OVERVIEW_STATUSES.map((option) => (
          <MenuItem key={option} value={option}>
            {getStatusLabel(option)}
          </MenuItem>
        ))}
      </TextField>

      {!!onUploadStatusChange && (
        <TextField
          select
          size="small"
          label="Braze"
          value={uploadStatus ?? ''}
          onChange={(event) => onUploadStatusChange(event.target.value as TranslationOverviewUploadFilter | '')}
          sx={{ minWidth: 190 }}
          helperText="Rows with at least one pair in this upload state"
        >
          <MenuItem value="">Any upload state</MenuItem>
          {TRANSLATION_OVERVIEW_UPLOAD_FILTERS.map((option) => (
            <MenuItem key={option} value={option}>
              {getUploadStateLabel(option)}
            </MenuItem>
          ))}
        </TextField>
      )}

      {!!onBrandChange && (
        <TextField
          select
          size="small"
          label="Brand"
          value={brand ?? ''}
          onChange={(event) => onBrandChange(event.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All brands</MenuItem>
          {(brandOptions ?? []).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );
}
